import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtDMY } from "@/lib/format";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export const ROSTER_LEAVE = ["Annual leave", "Sick leave", "Bereavement leave", "Family violence leave", "Unpaid leave",
  "Time off in lieu", "Alternative holiday", "Training / course", "Jury service", "ACC / injury"];

const KIND_CLASS = {
  on: "bg-emerald-100 text-emerald-900 border-emerald-300",
  added: "bg-emerald-200 text-emerald-950 border-emerald-400",
  off: "bg-slate-100 text-slate-500 border-slate-200",
  ph_off: "bg-amber-100 text-amber-900 border-amber-300",
  ph_worked: "bg-amber-300 text-amber-950 border-amber-500",
  leave: "bg-violet-100 text-violet-900 border-violet-300",
  absence: "bg-rose-100 text-rose-900 border-rose-300",
};
const ON_KINDS = ["on", "added", "ph_worked"];
const daysOn = (c) => Object.values(c.cells).filter((cell) => ON_KINDS.includes(cell?.kind)).length;

export function useRoster(endpoint, date) {
  return useQuery({
    queryKey: ["roster", endpoint, date],
    queryFn: async () => (await api.get(`${endpoint}?date=${date}`)).data,
    refetchInterval: 30000,
  });
}

export const RosterTable = forwardRef(function RosterTable({ date, editable, endpoint, caption = false }, ref) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data, isLoading } = useRoster(endpoint, date);
  const setCell = useMutation({
    mutationFn: ({ cleanerId, d, value }) => api.post(`/cleaners/${cleanerId}/roster-cell`, { date: d, value }),
    onSuccess: () => {
      ["roster", "roster-pending", "cleaners", "roster-day", "roster-day-labels", "timeline", "cleans", "timesheets"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setEditing(null);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const periodDays = data ? data.days.length : 7;
  const canEdit = editable && data && !data.locked;
  return (
    <div className="overflow-x-auto rounded-md border bg-card" data-testid={`roster-period-${data?.start || date}`}>
      {caption && data && (
        <div className="flex items-center gap-2 border-b bg-secondary/30 px-2 py-1 text-xs font-semibold" data-testid={`roster-period-caption-${data.start}`}>
          {fmtDMY(data.start)} – {fmtDMY(data.end)}
          {data.locked && <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] uppercase text-slate-700"><Lock className="h-3 w-3" /> {t("Locked")}</span>}
          {data.days.some((d) => d.today) && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] uppercase text-indigo-800">{t("Current")}</span>}
        </div>
      )}
      <table ref={ref} className="w-full table-fixed border-collapse text-xs" data-testid="roster-table">
        <colgroup>
          <col style={{ width: "22%" }} />
          {Array.from({ length: periodDays }, (_, i) => <col key={i} style={{ width: `${68 / periodDays}%` }} />)}
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead>
          <tr className="bg-secondary/60">
            <th className="sticky left-0 z-10 bg-secondary/60 px-2 py-1.5 text-left font-semibold">{t("Cleaner")}</th>
            {(data?.days || []).map((d) => (
              <th key={d.date} className={`px-1 py-1.5 text-center align-top font-semibold ${d.today ? "bg-indigo-100 text-indigo-900" : ""} ${d.past ? "text-muted-foreground" : ""}`} data-testid={`roster-day-${d.date}`}>
                <div>{t(d.weekday)}</div>
                <div className="font-normal">{d.date.slice(8)}/{d.date.slice(5, 7)}</div>
                <div className="h-3 truncate text-[9px] font-semibold text-amber-700" title={d.holiday || undefined}>{d.holiday ? "PH" : ""}</div>
              </th>
            ))}
            <th className="px-2 py-1.5 text-center font-semibold" data-testid="roster-days-on-head">{t("Days on")}</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td className="px-2 py-3 text-muted-foreground" colSpan={periodDays + 2}>{t("Loading…")}</td></tr>}
          {(data?.cleaners || []).map((c) => (
            <tr key={c.id} className="border-t" data-testid={`roster-row-${c.id}`}>
              <td className="sticky left-0 z-10 truncate whitespace-nowrap bg-card px-2 py-1 font-medium" title={c.name}>{c.name}</td>
              {data.days.map((d) => {
                const cell = c.cells[d.date];
                const isEditing = editing && editing.cleanerId === c.id && editing.date === d.date;
                return (
                  <td key={d.date} className={`p-0.5 text-center ${d.today ? "bg-indigo-50/60" : ""}`} data-testid={`roster-cell-${c.id}-${d.date}`}>
                    {isEditing && canEdit ? (
                      <select autoFocus defaultValue="" onBlur={() => setEditing(null)}
                        onChange={(e) => e.target.value && setCell.mutate({ cleanerId: c.id, d: d.date, value: e.target.value })}
                        className="h-7 w-full rounded border bg-background text-[11px]" data-testid={`roster-cell-select-${c.id}-${d.date}`}>
                        <option value="">{t("Set…")}</option>
                        <option value="on">{t("ON")}</option>
                        <option value="off">{t("OFF")}</option>
                        {ROSTER_LEAVE.map((l) => <option key={l} value={l}>{t(l)}</option>)}
                      </select>
                    ) : (
                      <button type="button" disabled={!canEdit} onClick={() => setEditing({ cleanerId: c.id, date: d.date })} title={cell?.tag}
                        className={`w-full truncate rounded border px-1 py-1 text-[11px] font-semibold ${KIND_CLASS[cell?.kind] || ""} ${data.locked ? "opacity-50" : d.past ? "opacity-75" : ""} ${canEdit ? "cursor-pointer hover:brightness-95 hover:ring-1 hover:ring-indigo-400" : ""}`}
                        data-testid={`roster-tag-${c.id}-${d.date}`}>
                        {t(cell?.tag || "")}
                      </button>
                    )}
                  </td>
                );
              })}
              <td className="px-2 py-1 text-center font-semibold" data-testid={`roster-days-on-${c.id}`}>{daysOn(c)}</td>
            </tr>
          ))}
          {data && data.cleaners.length > 0 && (
            <tr className="border-t bg-secondary/40 font-semibold" data-testid="roster-count-row">
              <td className="sticky left-0 z-10 bg-secondary/40 px-2 py-1.5">{t("On shift")}</td>
              {data.days.map((d) => {
                const n = data.on_counts ? (data.on_counts[d.date] ?? 0) : data.cleaners.filter((c) => ON_KINDS.includes(c.cells[d.date]?.kind)).length;
                return <td key={d.date} className={`py-1.5 text-center ${n === 0 ? "text-rose-600" : ""}`} data-testid={`roster-count-${d.date}`}>{n}</td>;
              })}
              <td className="py-1.5 text-center" data-testid="roster-days-on-total">
                {data.on_counts ? Object.values(data.on_counts).reduce((s, n) => s + n, 0) : data.cleaners.reduce((s, c) => s + daysOn(c), 0)}
              </td>
            </tr>
          )}
          {data && data.cleaners.length === 0 && <tr><td className="px-2 py-3 italic text-muted-foreground" colSpan={periodDays + 2}>{t("No active cleaners.")}</td></tr>}
        </tbody>
      </table>
    </div>
  );
});
